import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ExternalLink, Globe, Menu, Phone, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { useSettings } from "@/lib/hooks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { to: "/anasayfa", key: "nav.home" },
  { to: "/tarifeler", key: "nav.packages" },
  { to: "/kampanyalar", key: "nav.campaigns" },
  { to: "/hizmetler", key: "nav.services" },
  { to: "/blog", key: "nav.blog" },
  { to: "/sss", key: "nav.faq" },
  { to: "/iletisim", key: "nav.contact" },
];

export const Logo = ({ dark }) => (
  <Link to="/anasayfa" data-testid="navbar-logo" className="flex items-center shrink-0">
    <img src="/cagrilogo.png" alt="ÇağrıNET" className="h-8 w-auto brightness-0 invert" />
  </Link>
);

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { data: settings } = useSettings();
  const [open, setOpen] = useState(false);
  const onlineUrl = settings?.online_islemler_url || "https://oim.cagrinetwork.com.tr";
  const phone = settings?.phone || "+90 850 303 5621";

  return (
    <header className="fixed top-0 z-50 w-full" data-testid="navbar">
      {/* Top utility bar */}
      <div className="hidden bg-[#021616] md:block">
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-between px-4 text-xs sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/anasayfa" data-testid="navbar-individual" className="font-medium text-white transition-colors hover:text-[#2DD4BF]" style={{ borderBottom: "2px solid #2DD4BF", paddingBottom: "2px" }}>
              {t("nav.individual")}
            </Link>

          </div>
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            className="flex items-center gap-1.5 font-medium text-zinc-300 transition-colors hover:text-[#2DD4BF]"
            data-testid="navbar-phone"
          >
            <Phone className="h-3.5 w-3.5" />
            {phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-[#062B2B]/80 backdrop-blur-lg border-b border-white/5">
        <div className="container mx-auto flex h-[68px] items-center justify-between px-3 sm:px-6 lg:px-8">
          <Logo />

          <nav className="hidden items-center gap-0.5 lg:flex" data-testid="navbar-links">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-lg px-3.5 py-2 text-[13px] font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-white/10 text-[#2DD4BF]"
                      : "text-zinc-300 hover:bg-white/5 hover:text-white"
                  }`
                }
                data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="language-switcher"
                className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-2 text-xs font-bold text-white transition-colors hover:border-[#2DD4BF]/50 hover:text-[#2DD4BF]"
              >
                <Globe className="h-4 w-4" />
                {LANGS.find((l) => l.code === lang)?.short}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`language-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={lang === l.code ? "bg-zinc-100 font-bold" : ""}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              href={onlineUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="online-islemler-button"
              className="hidden items-center gap-1.5 rounded-lg bg-[#2DD4BF] px-4 py-2 text-xs font-bold text-[#042E2E] transition-all hover:bg-[#14B8A6] sm:flex"
            >
              {t("nav.online")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>

            <a
              href="https://cagrinetwork.com.tr/online_basvuru/#/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="online-basvuru-button"
              className="hidden items-center gap-1.5 rounded-lg border border-[#2DD4BF] px-4 py-2 text-xs font-bold text-[#2DD4BF] transition-all hover:bg-[#2DD4BF] hover:text-[#042E2E] sm:flex"
            >
              {t("nav.applyOnline")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white transition-colors hover:bg-white/20 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav className="bg-[#062B2B] px-4 py-4 shadow-xl lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                    isActive ? "bg-white/10 text-[#2DD4BF]" : "text-zinc-300 hover:bg-white/5"
                  }`
                }
                onClick={() => setOpen(false)}
              >
                {t(item.key)}
              </NavLink>
            ))}
            <a
              href={onlineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-[#2DD4BF] px-5 py-3 text-sm font-bold text-[#042E2E]"
            >
              {t("nav.online")}
              <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href="https://cagrinetwork.com.tr/online_basvuru/#/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 rounded-lg border border-[#2DD4BF] px-5 py-3 text-sm font-bold text-[#2DD4BF]"
            >
              {t("nav.applyOnline")}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
